import React from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { Box, Link as MuiLink, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import "katex/dist/katex.min.css";

export interface MarkdownRendererProps {
  children: string | null | undefined;
  variant?: "block" | "inline";
  sx?: SxProps<Theme>;
  align?: "left" | "right" | "center";
  isUserMessage?: boolean;
}

/**
 * MarkdownRenderer
 * - Adds remark-gfm for tables, task lists, strikethrough, autolinks
 * - Provides sensible default styling for tables, code, lists, and blockquotes
 * - Supports inline rendering (for mixed inline text/link flows)
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ children, variant = "block", sx, align, isUserMessage = false }) => {
  if (!children) return null;

  const isInline = variant === "inline";

  // Inline components: render paragraphs as span to keep inline flow
  const inlineComponents = {
    p: ({ children, ...props }: any) => <span {...props}>{children}</span>,
    // Keep links as MUI links even in inline mode
    a: ({ href, children, ...props }: any) => (
      <MuiLink href={href} target="_blank" rel="noopener noreferrer" underline="hover" {...props}>
        {children}
      </MuiLink>
    ),
  } as const;

  // Block components: map common tags to MUI and style code blocks
  const blockComponents = {
    p: ({ children, ...props }: any) => (
      <Typography 
        variant="body2" 
        component="p" 
        sx={isUserMessage ? { color: 'inherit' } : undefined}
        {...props}
      >
        {children}
      </Typography>
    ),
    h1: ({ children, ...props }: any) => (
      <Typography 
        variant="subtitle1" 
        component="h1" 
        fontWeight={500}
        sx={{ fontSize: '1.1rem', mb: 1, mt: 0.5 }}
        {...props}
      >
        {children}
      </Typography>
    ),
    h2: ({ children, ...props }: any) => (
      <Typography 
        variant="subtitle2" 
        component="h2" 
        fontWeight={500}
        sx={{ fontSize: '1rem', mb: 0.75, mt: 0.5 }}
        {...props}
      >
        {children}
      </Typography>
    ),
    h3: ({ children, ...props }: any) => (
      <Typography 
        variant="body1" 
        component="h3" 
        fontWeight={500}
        sx={{ fontSize: '0.9rem', mb: 0.5, mt: 0.5 }}
        {...props}
      >
        {children}
      </Typography>
    ),
    a: ({ href, children, ...props }: any) => (
      <MuiLink href={href} target="_blank" rel="noopener noreferrer" underline="hover" {...props}>
        {children}
      </MuiLink>
    ),
    ul: ({ children, ...props }: any) => (
      <Box 
        component="ul" 
        sx={{ 
          my: 1, 
          pl: 2.5,
          listStyleType: 'disc',
          '& li': {
            mb: 0.25,
            lineHeight: 1.4
          }
        }} 
        {...props}
      >
        {children}
      </Box>
    ),
    ol: ({ children, ...props }: any) => (
      <Box 
        component="ol" 
        sx={{ 
          my: 1, 
          pl: 2.5,
          listStyleType: 'decimal',
          '& li': {
            mb: 0.25,
            lineHeight: 1.4
          }
        }} 
        {...props}
      >
        {children}
      </Box>
    ),
    li: ({ children, ...props }: any) => (
      <Box 
        component="li" 
        sx={{ 
          display: 'list-item',
          '& p': {
            m: 0,
            display: 'inline'
          }
        }} 
        {...props}
      >
        {children}
      </Box>
    ),
    // Wrap tables to allow horizontal scrolling on small screens
    table: ({ children, ...props }: any) => (
      <Box sx={{ overflowX: "auto", overflowY: "hidden", maxWidth: "100%", display: "block" }}>
        <table {...props}>{children}</table>
      </Box>
    ),
    code: ({ inline, children, ...props }: any) => (
      <Box
        component={inline ? "code" : "pre"}
        sx={inline ? {
          fontFamily: "monospace",
          fontSize: 12,
          bgcolor: "action.hover",
          borderRadius: 1,
          px: 0.5,
          display: "inline",
        } : {
          fontFamily: "monospace",
          fontSize: 12,
          bgcolor: "action.hover",
          borderRadius: 1,
          p: 1,
          overflowX: "auto",
          m: 0,
        }}
        {...props}
      >
        {children}
      </Box>
    ),
    blockquote: ({ children, ...props }: any) => (
      <Box
        component="blockquote"
        sx={(theme) => ({
          borderLeft: `4px solid ${theme.palette.divider}`,
          pl: 2,
          color: "text.secondary",
          m: 0,
          py: 0.5,
        })}
        {...props}
      >
        {children}
      </Box>
    ),
  } as const;

  const baseSx: SxProps<Theme> = {
    display: isInline ? "inline" : undefined,
    textAlign: align,
    // Apply white color for user messages at the MarkdownRenderer level
    ...(isUserMessage && {
      color: (theme: Theme) => theme.palette.semantic.text.inverted,
    }),
    // Paragraph spacing - using higher specificity to override parent Box styles
    "& .MuiTypography-root.MuiTypography-body2": { 
      margin: '0 !important',
      marginBottom: '8px !important'
    },
    "& .MuiTypography-root.MuiTypography-body2:last-child": { 
      marginBottom: '0 !important' 
    },
    // Fallback for any remaining p tags
    "& p": { margin: 0 },
    "& p:not(:last-child)": { marginBottom: 8 },
    // Heading spacing and hierarchy
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      '&:first-child': { marginTop: '0 !important' },
      '&:last-child': { marginBottom: '0 !important' },
    },
    // List spacing improvements
    "& ul, & ol": {
      '&:first-child': { marginTop: '0 !important' },
      '&:last-child': { marginBottom: '0 !important' },
      // Nested list spacing
      '& ul, & ol': {
        marginTop: '4px !important',
        marginBottom: '4px !important',
      }
    },
    "& li": {
      // Ensure consistent spacing between list items
      '&:last-child': { marginBottom: '0 !important' }
    },
    // Table styling
    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      marginTop: 2,
      marginBottom: 2,
    },
    "& th, & td": {
      border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
      padding: 2,
      verticalAlign: "top",
      textAlign: "left",
      lineHeight: 1.2,
    },
    // Ensure extra paragraph spacing inside cells is removed
    "& td p, & th p": { margin: 0 },
    "& thead th": {
      backgroundColor: "action.hover",
      fontWeight: 600,
      paddingTop: 2,
      paddingBottom: 2,
      lineHeight: 1.2,
    },
    "& tbody tr:nth-of-type(odd) td": {
      backgroundColor: "background.default",
    },
    // Target ONLY cells with explicit light backgrounds for dark text
    "& thead th, & tbody tr:nth-of-type(odd) td": {
      color: (theme: Theme) => theme.palette.semantic.text.paragraph,
    },
    "& thead th p, & tbody tr:nth-of-type(odd) td p": {
      color: (theme: Theme) => theme.palette.semantic.text.paragraph,
    },
    // Trim outer spacing for first/last child blocks
    "& > *:first-child": { marginTop: 0 },
    "& > *:last-child": { marginBottom: 0 },
    "& > table:first-of-type": { marginTop: 0 },
    "& > table:last-of-type": { marginBottom: 0 },
    // Slightly pull content up/down when table is at edges to counter card padding
    ":has(> table:first-of-type)": { marginTop: -4 },
    ":has(> table:last-of-type)": { marginBottom: -4 },
  };

  const mergedSx: SxProps<Theme> = sx
    ? (Array.isArray(sx) ? [baseSx, ...sx] : [baseSx, sx])
    : baseSx;

  return (
    <Box component={isInline ? "span" : "div"} sx={mergedSx}>
      <Markdown 
        remarkPlugins={[remarkGfm, remarkMath]} 
        rehypePlugins={[rehypeKatex]}
        components={isInline ? (inlineComponents as any) : (blockComponents as any)}
      >
        {children}
      </Markdown>
    </Box>
  );
};

export default MarkdownRenderer;

